import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, ButtonBase, IconButton, Paper, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { Rnd } from 'react-rnd';

// Sidebar width — must stay in sync with BusinessPage/Sidebar
export const SIDEBAR_WIDTH = 300;

// Header (96px) + button row (56px) = 152px
// Windows start below the "Ask Kaiylee" / "New Offer" button row
export const WINDOWS_TOP_OFFSET = 152;

const VIEWPORT_PADDING = 8;
const DEFAULT_WIDTH = 420;

const resizeHandleStyles = {
  right: { width: 10, right: 0, cursor: 'ew-resize' },
  bottom: { height: 10, bottom: 0, cursor: 'ns-resize' },
  bottomRight: { width: 22, height: 22, right: 0, bottom: 0, cursor: 'nwse-resize' },
};
const resizeDirections = {
  right: true,
  bottom: true,
  bottomRight: true,
};

function getInitialX(fixedLeft, fixedRight, width) {
  if (fixedRight === undefined) return fixedLeft ?? SIDEBAR_WIDTH;
  const numericWidth = typeof width === 'number' ? width : DEFAULT_WIDTH;
  if (typeof window === 'undefined') return VIEWPORT_PADDING;
  return Math.max(VIEWPORT_PADDING, window.innerWidth - fixedRight - numericWidth);
}

export default function ResizableWindow({
  id,
  title,
  subtitle,
  open,
  onClose,
  onMinimize,
  onRestore,
  minimized = false,
  minWidth = 360,
  minHeight = 320,
  width,
  height,
  topOffset = WINDOWS_TOP_OFFSET,
  fixedLeft,
  fixedRight,
  onSizeChange,
  children,
  accentColor = '#0e75bd',
}) {
  const initialWidth = width ?? DEFAULT_WIDTH;
  const initialHeight = height ?? (
    typeof window === 'undefined'
      ? minHeight
      : Math.max(minHeight, window.innerHeight - topOffset - VIEWPORT_PADDING)
  );
  const initialLeft = getInitialX(fixedLeft, fixedRight, initialWidth);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({
    width: initialWidth,
    height: initialHeight,
  });
  const handleResizeStop = (node) => {
    const resizedWidth = node.offsetWidth;
    const resizedHeight = node.offsetHeight;
    const nextSize = {
      width: node.style.width || resizedWidth,
      height: node.style.height || resizedHeight,
    };
    setSize(nextSize);
    onSizeChange?.({ width: resizedWidth, height: resizedHeight });
  };

  useEffect(() => {
    if (!open) return;
    setPosition({ x: 0, y: 0 });
    setSize({
      width: initialWidth,
      height: initialHeight,
    });
  }, [fixedLeft, initialHeight, initialWidth, open, topOffset]);

  if (!open || typeof document === 'undefined') return null;

  const clampPosition = (_, data) => {
    const windowWidth = data.node.offsetWidth;
    const windowHeight = data.node.offsetHeight;
    setPosition({
      x: Math.min(
        window.innerWidth - initialLeft - windowWidth - VIEWPORT_PADDING,
        Math.max(VIEWPORT_PADDING - initialLeft, data.x)
      ),
      y: Math.min(
        window.innerHeight - topOffset - windowHeight - VIEWPORT_PADDING,
        Math.max(VIEWPORT_PADDING - topOffset, data.y)
      ),
    });
  };
  const maxWidth = Math.max(
    minWidth,
    window.innerWidth - initialLeft - position.x - VIEWPORT_PADDING
  );
  const maxHeight = Math.max(
    minHeight,
    window.innerHeight - topOffset - position.y - VIEWPORT_PADDING
  );

  return createPortal(
    <>
    <Box
      data-testid={id ? `${id}-stage` : 'resizable-window-stage'}
      sx={{
        position: 'fixed',
        left: initialLeft,
        top: topOffset,
        width: initialWidth,
        height: `calc(100vh - ${topOffset}px)`,
        zIndex: 1400,
        pointerEvents: 'none',
      }}
    >
    <Rnd
      id={id}
      data-testid={id ? `${id}-window` : 'resizable-window'}
      size={size}
      position={position}
      minWidth={minWidth}
      minHeight={minHeight}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      onDrag={clampPosition}
      onDragStop={clampPosition}
      onResizeStop={(_, __, node) => handleResizeStop(node)}
      dragHandleClassName="resizable-window-titlebar"
      enableResizing={minimized ? false : resizeDirections}
      resizeHandleStyles={resizeHandleStyles}
      resizeHandleComponent={{
        bottomRight: (
          <Box
            aria-hidden="true"
            data-testid="resize-handle-bottom-right"
            sx={{
              position: 'absolute',
              right: 4,
              bottom: 4,
              width: 12,
              height: 12,
              opacity: 0.55,
              background: `linear-gradient(135deg, transparent 0 46%, ${accentColor} 47% 54%, transparent 55% 68%, ${accentColor} 69% 76%, transparent 77%)`,
              transition: 'opacity 160ms ease',
              '&:hover': { opacity: 0.9 },
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          />
        ),
      }}
      style={{
        position: 'absolute',
        display: minimized ? 'none' : 'flex',
        flexDirection: 'column',
        pointerEvents: 'auto',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: '16px 16px 0 0',
          border: '1px solid rgba(0,0,0,0.10)',
          borderBottom: 'none',
          overflow: 'hidden',
          bgcolor: '#fff',
          boxShadow: '0 -4px 32px rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          className="resizable-window-titlebar"
          aria-label={`Move ${title} window`}
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            userSelect: 'none',
            cursor: 'grab',
            flexShrink: 0,
            background:
              'linear-gradient(135deg, rgba(249,183,86,0.22) 0%, rgba(14,117,189,0.10) 60%, rgba(0,0,0,0.02) 100%)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: accentColor,
              flexShrink: 0,
            }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{
                fontWeight: 600,
                fontSize: '0.8rem',
                lineHeight: 1.3,
                color: '#1a1a1a',
                letterSpacing: '-0.01em',
              }}>
                {title}
              </Typography>
              {subtitle && (
                <Typography sx={{
                  color: 'rgba(0,0,0,0.45)',
                  fontSize: '0.68rem',
                  lineHeight: 1.2,
                }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {onMinimize && (
              <IconButton
                onClick={onMinimize}
                size="small"
                sx={windowControlSx}
                aria-label="Minimize window"
              >
                <RemoveRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            <IconButton
              onClick={onClose}
              size="small"
              sx={windowControlSx}
              aria-label="Close window"
            >
              <CloseRoundedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{children}</Box>
      </Paper>
    </Rnd>
    </Box>
    {minimized && (
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          right: 16,
          bottom: 16,
          zIndex: 1400,
          display: 'flex',
          alignItems: 'stretch',
          overflow: 'hidden',
          borderRadius: '14px',
          border: '1px solid rgba(0,0,0,0.10)',
          bgcolor: '#fff',
        }}
      >
        <ButtonBase
          onClick={onRestore}
          sx={{ px: 2, py: 1.25, gap: 1, color: '#1a1a1a' }}
          aria-label={`Restore ${title} window`}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: accentColor }} />
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{title}</Typography>
          <KeyboardArrowUpRoundedIcon sx={{ fontSize: 18, color: '#0e75bd' }} />
        </ButtonBase>
        <IconButton
          onClick={onClose}
          sx={{ borderRadius: 0, px: 1.25, borderLeft: '1px solid rgba(0,0,0,0.08)' }}
          aria-label="Close minimized window"
        >
          <CloseRoundedIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Paper>
    )}
    </>,
    document.body
  );
}

const windowControlSx = {
  width: 24,
  height: 24,
  bgcolor: 'rgba(0,0,0,0.04)',
  '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
};
